import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { connectFirestoreEmulator, initializeFirestore, type Firestore } from 'firebase/firestore';

/**
 * Firebase initialization.
 *
 * Reads from VITE_FIREBASE_* env vars (see .env.example). If the API
 * key is missing, exports `undefined` for `app/auth/firestore/storage`
 * and logs a single warning so the UI still renders during early
 * development.
 *
 * The two `VITE_USE_*` flags decide whether we *use* the SDK once
 * initialized, so the swap is gated by env without code changes:
 *   - VITE_USE_FIREBASE_AUTH  → AuthProvider switches to Firebase
 *   - VITE_USE_FIRESTORE      → db adapter switches to firestoreStore
 */

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let storage: FirebaseStorage | undefined;
let firestore: Firestore | undefined;

if (cfg.apiKey) {
  app = initializeApp(cfg);
  auth = getAuth(app);
  storage = getStorage(app);
  // `ignoreUndefinedProperties` lets us write records that have optional
  // fields left undefined (e.g. an account with no pocName) without
  // Firestore throwing "Unsupported field value: undefined".
  firestore = initializeFirestore(app, { ignoreUndefinedProperties: true });
} else if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Argo OS] Firebase env vars missing — running without Firebase. ' +
      'Copy .env.example to .env.local and fill in VITE_FIREBASE_* to enable auth + Firestore + storage.',
  );
}

/**
 * Emulator mode — the real SDK + real rules/functions, pointed at
 * localhost (see LOCAL-EMULATOR-SETUP.md). Gated on import.meta.env.DEV
 * so a production build can NEVER connect to localhost, and on an
 * explicit opt-in flag so plain `npm run dev` stays on the mock store.
 */
export const useEmulators =
  import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';

if (useEmulators && auth && firestore && storage) {
  // Must run immediately after init and before any read/write — this is
  // the single init module imported everywhere, so here is sufficient.
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  // eslint-disable-next-line no-console
  console.info('[Argo OS] Connected to local Firebase emulators.');
}

/** True when both the env config AND the explicit opt-in flag are set. */
export const useFirestore =
  Boolean(cfg.apiKey) && import.meta.env.VITE_USE_FIRESTORE === 'true';
export const useFirebaseAuth =
  Boolean(cfg.apiKey) && import.meta.env.VITE_USE_FIREBASE_AUTH === 'true';

export { app, auth, storage, firestore };
